import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { PhoneOff } from "lucide-react";
import { api } from "@/lib/api";
import { useMe } from "@/hooks/queries";
import { useOnline } from "@/hooks/useOnline";
import { useVoiceSession } from "@/hooks/useVoiceSession";
import { JarvisPresence } from "@/components/voice/JarvisPresence";
import { VoiceStatus } from "@/components/voice/VoiceStatus";
import { VoiceRates } from "@/components/voice/VoiceRates";
import { VoiceUsage } from "@/components/voice/VoiceUsage";
import { VoiceTranscript } from "@/components/voice/VoiceTranscript";
import { VoiceNotice } from "@/components/voice/VoiceNotice";
import { VoicePicker } from "@/components/voice/VoicePicker";

export default function VoicePage() {
  const config = useQuery({ queryKey: ["voice-config"], queryFn: () => api.get("/voice/config"), staleTime: 60_000 });
  const { data: me } = useMe();
  const online = useOnline();
  const presenceRef = useRef(null);
  const levelRef = useRef({ kind: null, value: 0, playing: false });
  const voice = me?.settings?.voice || config.data?.default_voice || "warm";
  const v = useVoiceSession({ voice, presenceRef, levelRef });
  const available = config.data?.available;
  const balance = v.balance ?? me?.credits?.balance ?? null;
  const active = v.status !== "idle" || (v.turns.length > 0 && !v.ended);
  const used = v.usage.listenCredits + v.usage.speakCredits + v.usage.replyCredits > 0;

  return (
    <div className="h-full overflow-y-auto" data-testid="voice-page">
      <div className="mx-auto flex w-full max-w-[760px] flex-col px-4 pb-20 pt-8 sm:px-6 sm:pt-12 lg:max-w-[1120px]">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[1.7rem] font-semibold tracking-tight text-white sm:text-3xl">JARVIS</h1>
            <p className="mt-1 text-sm text-zinc-500">Your voice assistant. Tap to talk — every reply is spoken and written down.</p>
          </div>
          <VoicePicker voices={config.data?.voices} value={voice} disabled={!available} />
        </header>
        {!online && <p className="mt-6 rounded-xl border border-amber-400/25 bg-amber-500/[0.07] px-4 py-3 text-sm text-amber-100" data-testid="voice-offline">You're offline. Voice will be back when your connection returns.</p>}
        <div className="lg:mt-8 lg:grid lg:grid-cols-[400px_minmax(0,1fr)] lg:items-start lg:gap-12">
        <section className="mt-8 flex flex-col items-center lg:sticky lg:top-8 lg:mt-0">
          <JarvisPresence ref={presenceRef} levelRef={levelRef} status={v.status} onTap={v.tap} disabled={!available || !online || v.status === "transcribing"} />
          <VoiceStatus status={v.status} elapsed={v.elapsed} available={available} />
          {active && (
            <button type="button" onClick={v.end} className="mt-3 inline-flex h-9 items-center gap-2 rounded-full border border-white/12 px-4 text-[13px] text-zinc-300 transition-colors hover:border-white/25 hover:text-white" data-testid="voice-end-session">
              <PhoneOff className="h-3.5 w-3.5" /> End session
            </button>
          )}
          {v.ended && <p className="mt-3 text-xs text-zinc-500" data-testid="voice-session-ended">Session ended. Tap to start a new one.</p>}
        </section>
        <div className="mt-8 space-y-5 lg:mt-0">
          <VoiceNotice notice={v.notice} onRetry={v.retry} onDismiss={v.dismiss} />
          <VoiceTranscript turns={v.turns} />
          {(used || v.turns.length > 0) && <VoiceUsage usage={v.usage} balance={balance} conversationId={v.conversationId} />}
          {!v.turns.length && <VoiceRates config={config.data} balance={balance} />}
        </div>
        </div>
      </div>
    </div>
  );
}
